import { apiGet } from "./authDevApiClient.js";
import { tokenManager } from "../utils/tokenManager.js";

/**
 * @typedef {Object} ModuleCrudAccess
 * @property {boolean} read
 * @property {boolean} create
 * @property {boolean} update
 * @property {boolean} delete
 */

/**
 * @typedef {Object} AdministrationSections
 * @property {boolean} cameraManagement
 * @property {boolean} nvrManagement
 * @property {boolean} zoneManagement
 * @property {boolean} aiManagement
 * @property {boolean} alertRulesManagement
 * @property {boolean} userManagement
 * @property {boolean} [rolesAndPermissions]
 */

/**
 * @typedef {Object} AccountAccess
 * @property {string} userId
 * @property {string} [systemRole]
 * @property {boolean} isAdmin
 * @property {boolean} isSuperAdmin
 * @property {boolean} isActive
 * @property {boolean} canManageUsers
 * @property {boolean} canManageRoles
 * @property {boolean} canCreateCameras
 * @property {Record<string, ModuleCrudAccess>} modules
 * @property {AdministrationSections | null} administrationSections
 * @property {string[] | null} readableCameraIds
 */

const MODULE_KEYS = [
  "omniwatch",
  "ai_detection",
  "alert_service",
  "flashback",
  "camera_management",
  "nvr_management",
  "zone_management",
  "ai_management",
  "alert_rules_management",
  "recycle_bin_management",
  "user_management",
];

const LEGACY_MODULE_ALIASES = {
  cameras: "camera_management",
  users: "user_management",
  roles: "user_management",
  nvr: "nvr_management",
  recycle_bin: "recycle_bin_management",
  "recycle-bin": "recycle_bin_management",
  recycle_bin_mgmt: "recycle_bin_management",
  "recycle-bin-mgmt": "recycle_bin_management",
};

function normalizeModuleCrud(raw) {
  if (!raw || typeof raw !== "object") {
    return { read: false, create: false, update: false, delete: false };
  }
  const o = raw;
  return {
    read: Boolean(o.read ?? o.canRead ?? o.can_read),
    create: Boolean(o.create ?? o.canCreate ?? o.can_create),
    update: Boolean(o.update ?? o.canUpdate ?? o.can_update),
    delete: Boolean(o.delete ?? o.canDelete ?? o.can_delete),
  };
}

function normalizeModuleKey(key) {
  const normalized = String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (MODULE_KEYS.includes(normalized)) return normalized;
  return LEGACY_MODULE_ALIASES[normalized] ?? null;
}

function normalizeModules(raw) {
  const modules = {};
  if (!raw || typeof raw !== "object") return modules;

  for (const [key, value] of Object.entries(raw)) {
    const canonical = normalizeModuleKey(key);
    if (!canonical || !value || typeof value !== "object") continue;
    const crud = normalizeModuleCrud(value);
    const existing = modules[canonical];
    if (!existing) {
      modules[canonical] = crud;
      continue;
    }
    modules[canonical] = {
      read: existing.read || crud.read,
      create: existing.create || crud.create,
      update: existing.update || crud.update,
      delete: existing.delete || crud.delete,
    };
  }

  // Legacy `cameras` row applies to Oomni Watch + Administration camera mgmt
  if (raw.cameras && typeof raw.cameras === "object") {
    const legacy = normalizeModuleCrud(raw.cameras);
    for (const key of ["omniwatch", "camera_management"]) {
      const existing = modules[key];
      if (!existing) {
        modules[key] = legacy;
      } else {
        modules[key] = {
          read: existing.read || legacy.read,
          create: existing.create || legacy.create,
          update: existing.update || legacy.update,
          delete: existing.delete || legacy.delete,
        };
      }
    }
  }

  return modules;
}

const ADMIN_SECTION_ALIASES = {
  cameraManagement: "cameraManagement",
  camera_management: "cameraManagement",
  nvrManagement: "nvrManagement",
  nvr_management: "nvrManagement",
  zoneManagement: "zoneManagement",
  zone_management: "zoneManagement",
  aiManagement: "aiManagement",
  ai_management: "aiManagement",
  alertRulesManagement: "alertRulesManagement",
  alert_rules_management: "alertRulesManagement",
  recycleBinManagement: "recycleBinManagement",
  recycle_bin_management: "recycleBinManagement",
  recycle_bin: "recycleBinManagement",
  "recycle-bin-mgmt": "recycleBinManagement",
  userManagement: "userManagement",
  user_management: "userManagement",
  rolesAndPermissions: "rolesAndPermissions",
  roles_and_permissions: "rolesAndPermissions",
};

function normalizeAdministrationSections(raw) {
  if (!raw || typeof raw !== "object") return null;
  const out = {
    cameraManagement: false,
    nvrManagement: false,
    zoneManagement: false,
    aiManagement: false,
    alertRulesManagement: false,
    recycleBinManagement: false,
    userManagement: false,
    rolesAndPermissions: false,
  };
  let hasAny = false;
  for (const [key, value] of Object.entries(raw)) {
    const canonical = ADMIN_SECTION_ALIASES[key];
    if (!canonical) continue;
    out[canonical] = Boolean(value);
    hasAny = true;
  }
  return hasAny ? out : null;
}

function unwrapAccessPayload(body) {
  if (body && typeof body === "object" && body.data && typeof body.data === "object") {
    return body.data;
  }
  return body && typeof body === "object" ? body : null;
}

function normalizeAccess(raw) {
  if (!raw || typeof raw !== "object") return null;
  const o = raw;
  const ids = o.readableCameraIds ?? o.readable_camera_ids;
  const systemRole = String(o.systemRole ?? o.system_role ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  const isSuperAdmin = Boolean(
    o.isSuperAdmin ??
      o.is_super_admin ??
      (systemRole === "super_admin" || systemRole === "superadmin"),
  );
  return {
    userId: String(o.userId ?? o.user_id ?? "").trim(),
    systemRole: systemRole || undefined,
    isAdmin: Boolean(o.isAdmin ?? o.is_admin) || isSuperAdmin,
    isSuperAdmin,
    isActive: o.isActive !== false && o.is_active !== false,
    canManageUsers: Boolean(o.canManageUsers ?? o.can_manage_users),
    canManageRoles: Boolean(o.canManageRoles ?? o.can_manage_roles),
    canCreateCameras: Boolean(o.canCreateCameras ?? o.can_create_cameras),
    modules: normalizeModules(o.modules),
    administrationSections: normalizeAdministrationSections(
      o.administrationSections ?? o.administration_sections,
    ),
    readableCameraIds: Array.isArray(ids)
      ? ids.map((id) => String(id))
      : ids === null || ids === undefined
        ? null
        : null,
  };
}

/** In-flight dedupe — login + App refresh (or StrictMode) can race. */
let accountAccessInFlight = null;

/**
 * After hard refresh the httpOnly cookie authenticates Node, but cross-origin
 * AI/Alert calls still need a tab-scoped Bearer. Node echoes the cookie oat as
 * `sessionToken` (not for localStorage).
 */
function hydrateSessionTokenFromAccessBody(body) {
  const payload = unwrapAccessPayload(body);
  const sessionToken =
    (payload && typeof payload.sessionToken === "string" && payload.sessionToken) ||
    (body && typeof body.sessionToken === "string" && body.sessionToken) ||
    "";
  const trimmed = String(sessionToken).trim();
  if (!trimmed) return;
  const user = tokenManager.getUser();
  tokenManager.setToken(trimmed, user);
}

/** GET /api/account/access */
export async function fetchAccountAccess() {
  if (accountAccessInFlight) {
    return accountAccessInFlight;
  }

  accountAccessInFlight = (async () => {
    const body = await apiGet("/api/account/access");
    hydrateSessionTokenFromAccessBody(body);
    return normalizeAccess(unwrapAccessPayload(body));
  })();

  try {
    return await accountAccessInFlight;
  } finally {
    accountAccessInFlight = null;
  }
}
